import React, { useState, useMemo } from 'react';
import { Modal, Typography, Button, Space, Collapse, Input, Select, Row, Col, Statistic, Card } from 'antd';
import { SearchOutlined, FilterOutlined, SortAscendingOutlined } from '@ant-design/icons';
import CompactParameterVisualization from './CompactParameterVisualization';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

/**
 * Compact Parameter Modal Component
 * Space-efficient design for viewing hundreds of parameters
 * Maintains all functionality while optimizing for scalability
 */
const CompactParameterModal = ({
  visible,
  onCancel,
  title,
  parametersData,
  priorityThresholds = {},
  sfrScores = null
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  // Removed sortBy state - always using priority-based sorting

  // Priority color mapping
  const priorityColors = {
    High: '#ff4d4f',
    Moderate: '#fa8c16',
    Low: '#1890ff',
    Normal: '#52c41a',
    'No Data': '#d9d9d9'
  };

  // Process and flatten parameters data for filtering and sorting
  const processedParameters = useMemo(() => {
    if (!parametersData || typeof parametersData !== 'object') return [];

    let allParams = [];
    
    // Handle different data structures
    if (Array.isArray(parametersData)) {
      allParams = parametersData;
    } else {
      // Flatten nested structure
      Object.entries(parametersData).forEach(([groupKey, groupData]) => {
        if (Array.isArray(groupData)) {
          allParams.push(...groupData.map(param => ({ ...param, group: groupKey })));
        } else if (typeof groupData === 'object') {
          Object.entries(groupData).forEach(([subKey, subData]) => {
            if (Array.isArray(subData)) {
              allParams.push(...subData.map(param => ({ 
                ...param, 
                group: groupKey, 
                subgroup: subKey 
              })));
            }
          });
        }
      });
    }

    return allParams.map(param => ({
      ...param,
      hasData: param.hasData !== false && param.zScore !== null && param.zScore !== undefined,
      displayName: param.name || param.parameterName || param.key,
      categoryDisplay: param.subcategory || param.category || param.group || 'Unknown'
    }));
  }, [parametersData]);

  // Filter and sort parameters
  const filteredAndSortedParameters = useMemo(() => {
    let filtered = processedParameters;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(param =>
        param.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        param.categoryDisplay.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(param => {
        if (priorityFilter === 'high') return param.priority === 'High';
        if (priorityFilter === 'moderate') return param.priority === 'Moderate';
        if (priorityFilter === 'low') return param.priority === 'Low';
        if (priorityFilter === 'normal') return param.priority === 'Normal' || !param.priority;
        if (priorityFilter === 'nodata') return !param.hasData;
        return true;
      });
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(param => param.categoryDisplay === categoryFilter);
    }

    // PRIORITY-BASED SORTING: Always sort by priority score (specificImpact) first
    filtered.sort((a, b) => {
      // Primary sort: by specificImpact (priority score) - highest first
      const aImpact = a.specificImpact || 0;
      const bImpact = b.specificImpact || 0;
      if (aImpact !== bImpact) return bImpact - aImpact;

      // Secondary sort: by absolute Z-score for parameters with same impact
      const aAbsZ = Math.abs(a.zScore || 0);
      const bAbsZ = Math.abs(b.zScore || 0);
      if (aAbsZ !== bAbsZ) return bAbsZ - aAbsZ;

      // Tertiary sort: alphabetically by name
      return a.displayName.localeCompare(b.displayName);
    });

    return filtered;
  }, [processedParameters, searchTerm, priorityFilter, categoryFilter]);

  // Get unique categories for filter dropdown
  const uniqueCategories = useMemo(() => {
    return [...new Set(processedParameters.map(param => param.categoryDisplay))].sort();
  }, [processedParameters]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const total = processedParameters.length;
    const withData = processedParameters.filter(p => p.hasData).length;
    const highPriority = processedParameters.filter(p => p.priority === 'High').length;
    const moderatePriority = processedParameters.filter(p => p.priority === 'Moderate').length;
    
    return { total, withData, highPriority, moderatePriority };
  }, [processedParameters]);

  // REMOVED: Category-based grouping logic - now using unified priority-based list

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width="95vw"
      style={{ top: 10 }}
      styles={{ body: { maxHeight: '85vh', overflowY: 'auto', padding: '16px' } }}
    >
      {/* Summary Statistics */}
      <Card size="small" style={{ marginBottom: '16px', background: '#fafafa' }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic 
              title="Total Parameters" 
              value={summaryStats.total}
              valueStyle={{ fontSize: '18px', color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="With Data" 
              value={summaryStats.withData}
              valueStyle={{ fontSize: '18px', color: '#52c41a' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="High Priority" 
              value={summaryStats.highPriority}
              valueStyle={{ fontSize: '18px', color: '#ff4d4f' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="Moderate Priority" 
              value={summaryStats.moderatePriority}
              valueStyle={{ fontSize: '18px', color: '#fa8c16' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Filters and Controls */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={8}>
            <Search
              placeholder="Search parameters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={12} sm={4}>
            <Select
              value={priorityFilter}
              onChange={setPriorityFilter}
              style={{ width: '100%' }}
              size="small"
            >
              <Option value="all">All Priorities</Option>
              <Option value="high">High Priority</Option>
              <Option value="moderate">Moderate Priority</Option>
              <Option value="low">Low Priority</Option>
              <Option value="normal">Normal</Option>
              <Option value="nodata">No Data</Option>
            </Select>
          </Col>
          <Col xs={12} sm={4}>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              style={{ width: '100%' }}
              size="small"
            >
              <Option value="all">All Categories</Option>
              {uniqueCategories.map(cat => (
                <Option key={cat} value={cat}>{cat}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={8}>
            <Text strong style={{ color: '#1890ff' }}>
              📊 Priority-Based View: Parameters sorted by clinical impact score (highest priority first)
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Results Summary */}
      <div style={{ marginBottom: '12px' }}>
        <Text type="secondary">
          Showing {filteredAndSortedParameters.length} of {processedParameters.length} parameters
        </Text>
      </div>

      {/* Unified Priority-Based Parameter List */}
      {filteredAndSortedParameters.length > 0 ? (
        <Card
          size="small"
          title={`All Parameters (${filteredAndSortedParameters.length}) - Sorted by Priority Score`}
          style={{ background: '#fafafa' }}
        >
          <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '8px' }}>
            {filteredAndSortedParameters.map((param, index) => (
              <div key={param.key || index} style={{ marginBottom: '8px' }}>
                <CompactParameterVisualization
                  zScore={param.zScore}
                  currentValue={param.currentValue}
                  idealTarget={param.idealTarget}
                  unit={param.unit || ''}
                  parameterName={param.displayName}
                  priority={param.priority || 'Normal'}
                  specificImpact={param.specificImpact || 0}
                  category={param.categoryDisplay}
                  hasData={param.hasData}
                />
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <Text type="secondary">No parameters match your current filters.</Text>
        </div>
      )}
    </Modal>
  );
};

export default CompactParameterModal;
